/**
 * 새 오류 코드(이용 내역·권한·지원·계정 생존)의 공통 화면 문구 — W6 지시서 E2 표, 계약 SUPPORT_ERROR_CODES·PEP_ERROR_CODES.
 * axios 응답 가로채기가 standardErrorNotice() 결과를 같은 key 로 한 번 띄운다.
 * 부르는 쪽이 화면 안에서 직접 보이면 요청 config 에 showErrorNotice: false.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(tests/standardErrors.test.mjs).
 */

export const errorCode = (error) => error?.response?.data?.code;
const statusOf = (error) => error?.response?.status;

export const MESSAGES = {
  SUPPORT_SCOPE_DENIED: '지원 접속에서는 쓸 수 없는 메뉴입니다',
  SUPPORT_ACCOUNT_READONLY:
    '서티라이프(리셀러) 지원 계정은 고치거나 지울 수 없습니다.',
  SUPPORT_ACCOUNT_LOGIN_DENIED: '지원 계정은 이 화면으로 로그인할 수 없습니다.',
  ACCOUNT_INACTIVE: '계정이 정지되었거나 삭제되었습니다.',
  PERM_UNAVAILABLE: '잠시 후 다시 시도해 주세요.',
  AUDIT_UNAVAILABLE:
    '기록 장치 문제로 이 작업을 하지 않았습니다. 잠시 후 다시 시도해 주세요.',
  AUDIT_QUERY_TOO_BROAD: '조건을 좁혀 주세요(기간·회사).',
  TOO_MANY_REQUESTS: '잠시 후 다시 시도해 주세요.',
  MFA_ENROLL_REQUIRED:
    '이 고객사는 2차 인증 필수입니다. 「내 계정 보안」에서 수단을 먼저 등록하세요.',
  LEGACY_ACCESS_BLOCKED:
    '이전 방식은 막혔습니다. 「서브 관리자로 접속」을 쓰세요.',
  // 주소로 연, 메뉴에 안 보이는 화면 → 첫 화면으로 옮길 때(REDIRECT-REQ 4 — 옛 규칙만인 경우)
  ROUTE_DENIED: '이 메뉴를 볼 권한이 없습니다. 첫 화면으로 이동했어요.',
};

/** 옛 접근 길 막힘(CI-08): 403 SESSION_NOT_ALLOWED + reason LEGACY_ACCESS_BLOCKED */
export const isLegacyBlocked = (error) =>
  statusOf(error) === 403 &&
  errorCode(error) === 'SESSION_NOT_ALLOWED' &&
  error?.response?.data?.reason === 'LEGACY_ACCESS_BLOCKED';

/** 403 PERMISSION_DENIED 의 required(문자열 또는 배열) → 이름표로 바꾼 문장. 이름표가 없으면 코드 그대로 */
export const permissionDeniedText = (required, labels = {}) => {
  const codes = (Array.isArray(required) ? required : [required]).filter(
    Boolean
  );
  if (!codes.length) return '권한이 없습니다.';
  return `권한이 없습니다(필요: ${codes
    .map((c) => labels[c] || c)
    .join(', ')})`;
};

// 2FA API 자신(/auth/2fa/*)의 오류는 창 안에서 보이므로 공통 알림을 띄우지 않는다
const isTwoFactorRequest = (url = '') =>
  String(url).split('?')[0].includes('/auth/2fa/');

/**
 * 오류 응답 → 공통 알림. 처리 대상이 아니면 null.
 * @returns {{key: string, level: 'warning'|'error', text: string, logout?: boolean}|null}
 *   logout 이면 저장값을 지우고 로그인 화면으로(ACCOUNT_INACTIVE).
 */
export const standardErrorNotice = (error, labels = {}) => {
  const status = statusOf(error);
  const code = errorCode(error);
  const data = error?.response?.data || {};
  if (!status) return null;
  if (isLegacyBlocked(error)) {
    return {
      key: 'LEGACY_ACCESS_BLOCKED',
      level: 'warning',
      text: MESSAGES.LEGACY_ACCESS_BLOCKED,
    };
  }
  if (status === 401 && code === 'ACCOUNT_INACTIVE') {
    return {
      key: code,
      level: 'error',
      text: MESSAGES.ACCOUNT_INACTIVE,
      logout: true,
    };
  }
  if (status === 401 && code === 'SUPPORT_ACCOUNT_LOGIN_DENIED') {
    return { key: code, level: 'error', text: MESSAGES[code] };
  }
  if (
    status === 403 &&
    (code === 'PERMISSION_DENIED' || code === 'PERM_UNMARKED')
  ) {
    return {
      key: 'PERMISSION_DENIED',
      level: 'warning',
      text: permissionDeniedText(data.required, labels),
    };
  }
  if (
    status === 403 &&
    [
      'SUPPORT_SCOPE_DENIED',
      'SUPPORT_ACCOUNT_READONLY',
      'MFA_ENROLL_REQUIRED',
    ].includes(code)
  ) {
    return { key: code, level: 'warning', text: MESSAGES[code] };
  }
  if (
    status === 503 &&
    (code === 'PERM_UNAVAILABLE' || code === 'AUDIT_UNAVAILABLE')
  ) {
    return { key: code, level: 'error', text: MESSAGES[code] };
  }
  if (status === 400 && code === 'AUDIT_QUERY_TOO_BROAD') {
    return { key: code, level: 'warning', text: MESSAGES[code] };
  }
  if (
    status === 429 &&
    code !== 'OTP_RATE_LIMITED' &&
    !isTwoFactorRequest(error?.config?.url)
  ) {
    return {
      key: 'TOO_MANY_REQUESTS',
      level: 'warning',
      text: MESSAGES.TOO_MANY_REQUESTS,
    };
  }
  return null;
};
